import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { PumpEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadPumps,
  setAction,
  setPumpFilter,
  setSelectedPump,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManagePumpModalComponent,
  SearchPumpDrawerComponent,
} from './components'

export const PumpPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { pumpFilter, pumps, isPumpsLoaded } = useSelector(
    (state: RootState) => state.pump
  )
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [filter, setFilter] = React.useState(pumpFilter)

  React.useEffect(() => {
    setFilter(pumpFilter)
  }, [pumpFilter])

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadPumps())
    // eslint-disable-next-line
  }, [])

  const filteredPumps = React.useMemo(
    () =>
      pumps.filter(
        (pump) =>
          pump.name
            .toLowerCase()
            .includes((pumpFilter.name || '').toLowerCase()) &&
          pump.tank.station.name
            .toLowerCase()
            .includes((pumpFilter.stationName || '').toLowerCase()) &&
          pump.tank.name
            .toLowerCase()
            .includes((pumpFilter.tankName || '').toLowerCase()) &&
          pump.tank.fuel.name
            .toLowerCase()
            .includes((pumpFilter.fuelName || '').toLowerCase())
      ),
    [pumps, pumpFilter]
  )

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('pump.id', language),
        width: 20,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pump.name"
            value={filter.name}
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          />
        ),
        dataIndex: 'name',
        width: 100,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pump.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({ ...filter, stationName: e.target.value })
            }
          />
        ),
        render: (_: any, pump: PumpEntity) => pump.tank.station.name,
        width: 150,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pump.tank"
            value={filter.tankName}
            onChange={(e) => setFilter({ ...filter, tankName: e.target.value })}
          />
        ),
        render: (_: any, pump: PumpEntity) => pump.tank.name,
        width: 100,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pump.fuel"
            value={filter.fuelName}
            onChange={(e) => setFilter({ ...filter, fuelName: e.target.value })}
          />
        ),
        render: (_: any, pump: PumpEntity) => pump.tank.fuel.name,
        width: 100,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => dispatch(setPumpFilter(filter))}
          />
        ),
        render: (_: any, pump: PumpEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedPump(pump))
              dispatch(setAction(ActionEnum.VIEW))
            }}
            onDeleteButtonClick={() => {
              dispatch(setSelectedPump(pump))
              dispatch(setAction(ActionEnum.DELETE))
            }}
          />
        ),
        width: 120,
      },
    ],
    [language, filter, dispatch]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManagePumpModalComponent />}
      <PageTitleComponent
        onCreateButtonClick={() => {
          dispatch(setSelectedPump())
          dispatch(setAction(ActionEnum.CREATE))
        }}
        hasSearchDrawer
      />
      {management.showSearchDrawer && <SearchPumpDrawerComponent />}
      <TableComponent
        data={filteredPumps}
        columns={columns}
        loading={!isPumpsLoaded}
      />
    </div>
  )
}
